import type { PermissionDef, PermissionGroup, RoleItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useState } from "react";
import { del, errorMessage, get, patch, post } from "../../api";
import { Alert, Button, Dialog, Field, Loading, PageHeader, Panel, SelectField, cx } from "../../components/ui";

export function RolesPage() {
  const qc = useQueryClient();
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => get<RoleItem[]>("/api/roles") });
  const catalog = useQuery({
    queryKey: ["permissions"],
    queryFn: () => get<{ groups: PermissionGroup[]; permissions: PermissionDef[] }>("/api/permissions"),
  });

  // Draft permission sets per role, edited in the grid and saved together.
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  useEffect(() => {
    if (roles.data) setDraft(Object.fromEntries(roles.data.map((r) => [r.id, new Set(r.permissions)])));
  }, [roles.data]);

  const changed = useMemo(
    () =>
      (roles.data ?? []).filter((r) => {
        const d = draft[r.id];
        if (!d) return false;
        return d.size !== r.permissions.length || r.permissions.some((p) => !d.has(p));
      }),
    [roles.data, draft],
  );

  const [editing, setEditing] = useState<RoleItem | "new" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      for (const r of changed) {
        await patch(`/api/roles/${r.id}`, { name: r.name, description: r.description, permissions: [...draft[r.id]!] });
      }
    },
    onSuccess: async () => {
      setNotice(`Saved changes to ${changed.map((r) => r.name).join(", ")}.`);
      await qc.invalidateQueries({ queryKey: ["roles"] });
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: () => qc.invalidateQueries({ queryKey: ["roles"] }),
  });

  const toggle = (roleId: string, perm: string) => {
    setNotice(null);
    setDraft((d) => {
      const next = new Set(d[roleId]);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return { ...d, [roleId]: next };
    });
  };

  if (roles.isLoading || catalog.isLoading) return <Loading />;
  if (roles.error || catalog.error) return <Alert>{errorMessage(roles.error ?? catalog.error)}</Alert>;
  const list = roles.data!;
  const { groups, permissions } = catalog.data!;

  return (
    <div className="pb-24">
      <PageHeader
        title="Roles and permissions"
        description="Each column is a role. Check what that role can do, then save. Changes apply to everyone with the role."
        action={
          <Button variant="primary" onClick={() => setEditing("new")}>
            New role
          </Button>
        }
      />

      {notice && (
        <div className="mb-4">
          <Alert tone="ok">{notice}</Alert>
        </div>
      )}
      {save.error && (
        <div className="mb-4">
          <Alert>{errorMessage(save.error)}</Alert>
        </div>
      )}

      <Panel className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 w-[38%] border-b border-line bg-white px-5 py-4 text-left align-bottom text-sm font-semibold text-muted">
                Permission
              </th>
              {list.map((r) => (
                <th key={r.id} className="border-b border-line px-3 py-4 text-center align-bottom">
                  <button
                    onClick={() => setEditing(r)}
                    className="group inline-flex flex-col items-center rounded-md px-2 py-1 hover:bg-mist"
                    title={`Edit ${r.name}`}
                  >
                    <span className="font-semibold text-ink group-hover:text-brand">{r.name}</span>
                    <span className="text-xs font-normal text-muted">
                      {r.userCount} {r.userCount === 1 ? "person" : "people"}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const rows = permissions.filter((p) => p.group === group);
              if (!rows.length) return null;
              return (
                <Fragment key={group}>
                  <tr>
                    <td colSpan={list.length + 1} className="sticky left-0 bg-mist/70 px-5 py-2 text-sm font-semibold text-ink-soft">
                      {group}
                    </td>
                  </tr>
                  {rows.map((perm) => (
                    <tr key={perm.key} className="hover:bg-mist/40">
                      <td className="sticky left-0 z-10 border-b border-mist bg-white px-5 py-3">
                        <div className="font-medium">{perm.label}</div>
                        <div className="text-sm text-muted">{perm.available ? perm.description : `${perm.description} Takes effect when that feature ships.`}</div>
                      </td>
                      {list.map((r) => {
                        const on = draft[r.id]?.has(perm.key) ?? false;
                        const was = r.permissions.includes(perm.key);
                        return (
                          <td key={r.id} className={cx("border-b border-mist text-center", on !== was && "bg-brand-wash/60")}>
                            <input
                              type="checkbox"
                              className="size-[18px] cursor-pointer accent-[var(--color-brand)]"
                              checked={on}
                              onChange={() => toggle(r.id, perm.key)}
                              aria-label={`${r.name}: ${perm.label}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </Panel>

      {changed.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white/95 backdrop-blur lg:left-[232px]">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-8">
            <p className="text-sm text-ink-soft">
              Unsaved changes to <span className="font-semibold text-ink">{changed.map((r) => r.name).join(", ")}</span>
            </p>
            <div className="flex gap-2">
              <Button variant="quiet" onClick={() => setDraft(Object.fromEntries(list.map((r) => [r.id, new Set(r.permissions)])))}>
                Discard
              </Button>
              <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
                Save changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {editing && <RoleDialog role={editing === "new" ? null : editing} roles={list} onClose={() => setEditing(null)} onDone={setNotice} />}
    </div>
  );
}

function RoleDialog({ role, roles, onClose, onDone }: { role: RoleItem | null; roles: RoleItem[]; onClose: () => void; onDone: (msg: string) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [copyFrom, setCopyFrom] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const done = async (msg: string) => {
    await qc.invalidateQueries({ queryKey: ["roles"] });
    onDone(msg);
    onClose();
  };

  const saveRole = useMutation({
    mutationFn: () =>
      role
        ? patch(`/api/roles/${role.id}`, { name, description, permissions: role.permissions })
        : post("/api/roles", { name, description, permissions: roles.find((r) => r.id === copyFrom)?.permissions ?? [] }),
    onSuccess: () => done(role ? `Updated ${name}.` : `Created ${name}. Check its permissions in the grid.`),
  });
  const remove = useMutation({
    mutationFn: () => del(`/api/roles/${role!.id}`),
    onSuccess: () => done(`Deleted ${role!.name}.`),
  });
  const error = saveRole.error ?? remove.error;

  return (
    <Dialog
      title={role ? `Edit ${role.name}` : "New role"}
      onClose={onClose}
      footer={
        <>
          {role && !confirmDelete && (
            <Button variant="danger" className="mr-auto" onClick={() => setConfirmDelete(true)} disabled={role.userCount > 0}>
              Delete role
            </Button>
          )}
          {confirmDelete && (
            <Button variant="danger" className="mr-auto" busy={remove.isPending} onClick={() => remove.mutate()}>
              Yes, delete {role?.name}
            </Button>
          )}
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="role-form" busy={saveRole.isPending}>
            {role ? "Save" : "Create role"}
          </Button>
        </>
      }
    >
      <form
        id="role-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          saveRole.mutate();
        }}
      >
        {error && <Alert>{errorMessage(error)}</Alert>}
        <Field label="Name" required value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Field label="Description" value={description} onChange={(e) => setDescription(e.target.value)} hint="A short note on who this role is for." />
        {!role && (
          <SelectField label="Start with permissions from" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
            <option value="">No permissions</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </SelectField>
        )}
        {role && role.userCount > 0 && (
          <p className="text-sm text-muted">To delete this role, first remove it from the {role.userCount} {role.userCount === 1 ? "person" : "people"} who have it.</p>
        )}
      </form>
    </Dialog>
  );
}
